import * as React from 'react';
import { useIntersectionObserverRef } from '../../../../hooks/use-intersection-observer-ref';
import type {
	ISelectPaginationState,
	IUseSelectAsyncProps,
	IUseSelectAsyncResult,
	SelectLoadOperation,
	SelectLoadStatus,
	SelectOptionValue,
} from '../types';

interface IGetResolvedLoadStatusParams {
	internalLoadStatus: SelectLoadStatus;
	isError: boolean | undefined;
	isLoading: boolean | undefined;
}

const FIRST_PAGINATION_PAGE = 1;
const FIRST_NEXT_PAGE = FIRST_PAGINATION_PAGE + 1;

const getResolvedLoadStatus = ({
	internalLoadStatus,
	isError,
	isLoading,
}: IGetResolvedLoadStatusParams): SelectLoadStatus => {
	if (isLoading) {
		return 'loading';
	}

	if (isError) {
		return 'error';
	}

	return internalLoadStatus;
};

const getNextPaginationState = (pagination?: ISelectPaginationState): ISelectPaginationState => ({
	...pagination,
	page: pagination?.page === undefined ? FIRST_NEXT_PAGE : pagination.page + 1,
});

const copyPaginationState = (
	pagination?: ISelectPaginationState
): ISelectPaginationState | undefined => (pagination === undefined ? undefined : { ...pagination });

const arePaginationStatesEqual = (
	firstPagination?: ISelectPaginationState,
	secondPagination?: ISelectPaginationState
): boolean => {
	if (firstPagination === undefined || secondPagination === undefined) {
		return firstPagination === secondPagination;
	}

	return (
		firstPagination.cursor === secondPagination.cursor &&
		firstPagination.hasNextPage === secondPagination.hasNextPage &&
		firstPagination.page === secondPagination.page
	);
};

export const useSelectAsync = <TValue extends SelectOptionValue = SelectOptionValue, TMetadata = unknown>({
	hasNextPage,
	isError,
	isOpen = false,
	isLoading,
	lastOptionRef,
	loadOnOpen = false,
	loadOptions,
	onSearchChange,
	pagination,
	query,
}: IUseSelectAsyncProps<TValue, TMetadata>): IUseSelectAsyncResult => {
	const [internalLoadStatus, setInternalLoadStatus] = React.useState<SelectLoadStatus>('idle');
	const [internalPagination, setInternalPagination] = React.useState<ISelectPaginationState | undefined>(() =>
		copyPaginationState(pagination)
	);
	const [loadOperation, setLoadOperation] = React.useState<SelectLoadOperation>('options');
	const hasLoadedOnOpenRef = React.useRef(false);
	const hasNextPageRef = React.useRef(pagination?.hasNextPage ?? hasNextPage);
	const mountedRef = React.useRef(true);
	const paginationRef = React.useRef<ISelectPaginationState | undefined>(copyPaginationState(pagination));
	const paginationPropRef = React.useRef<ISelectPaginationState | undefined>(copyPaginationState(pagination));
	const queryRef = React.useRef(query);
	const requestIdRef = React.useRef(0);
	const skipNextCloseSearchLoadRef = React.useRef(false);
	const loadStatus = getResolvedLoadStatus({ internalLoadStatus, isError, isLoading });
	const resolvedHasNextPage = internalPagination?.hasNextPage ?? hasNextPage;
	const shouldUseInternalPaginationObserver = lastOptionRef === undefined;
	const canLoadNextPage = loadStatus !== 'loading' && loadStatus !== 'error';

	React.useEffect(() => {
		hasNextPageRef.current = resolvedHasNextPage;
	}, [resolvedHasNextPage]);

	React.useEffect(() => {
		queryRef.current = query;
	}, [query]);

	React.useEffect(() => {
		if (arePaginationStatesEqual(paginationPropRef.current, pagination)) {
			return;
		}

		const nextPagination = copyPaginationState(pagination);
		paginationPropRef.current = nextPagination;
		if (nextPagination !== undefined) {
			setInternalPagination(nextPagination);
			paginationRef.current = nextPagination;
		}
	}, [pagination]);

	React.useEffect(
		() => () => {
			mountedRef.current = false;
			requestIdRef.current += 1;
		},
		[]
	);

	const runLoadOptions = React.useCallback(
		async (
			nextQuery: string,
			nextPagination?: ISelectPaginationState,
			nextLoadOperation: SelectLoadOperation = 'options',
			fallbackPagination: ISelectPaginationState | undefined = nextPagination
		): Promise<boolean> => {
			const requestId = requestIdRef.current + 1;
			requestIdRef.current = requestId;
			setLoadOperation(nextLoadOperation);
			setInternalLoadStatus('loading');

			try {
				const result = await loadOptions({ pagination: nextPagination, query: nextQuery });

				if (!mountedRef.current || requestId !== requestIdRef.current) {
					return false;
				}

				const resolvedPagination =
					result.pagination === undefined
						? fallbackPagination
						: { ...fallbackPagination, ...result.pagination };
				setInternalPagination(resolvedPagination);
				paginationRef.current = resolvedPagination;
				setInternalLoadStatus('success');
				return false;
			} catch {
				if (!mountedRef.current || requestId !== requestIdRef.current) {
					return false;
				}

				setInternalLoadStatus('error');
				return true;
			}
		},
		[loadOptions]
	);

	const loadNextPage = React.useCallback(() => {
		if (!hasNextPageRef.current || !canLoadNextPage) {
			return;
		}

		void runLoadOptions(queryRef.current, getNextPaginationState(paginationRef.current), 'pagination');
	}, [canLoadNextPage, runLoadOptions]);

	const paginationObserverRef = useIntersectionObserverRef({
		enabled: shouldUseInternalPaginationObserver && Boolean(resolvedHasNextPage) && canLoadNextPage,
		onIntersect: loadNextPage,
	});

	const handleLastOptionRef = React.useCallback(
		(node: Element | null) => {
			lastOptionRef?.(node);
			paginationObserverRef(node);
		},
		[lastOptionRef, paginationObserverRef]
	);

	const loadOptionsOnOpen = React.useCallback(() => {
		if (!loadOnOpen || hasLoadedOnOpenRef.current) {
			return;
		}

		hasLoadedOnOpenRef.current = true;
		void runLoadOptions(queryRef.current, paginationRef.current).then(didFail => {
			if (didFail) {
				hasLoadedOnOpenRef.current = false;
			}
		});
	}, [loadOnOpen, runLoadOptions]);

	React.useEffect(() => {
		if (isOpen) {
			loadOptionsOnOpen();
		}
	}, [isOpen, loadOptionsOnOpen]);

	const handleOpenChange = React.useCallback(
		(nextIsOpen: boolean) => {
			if (!nextIsOpen) {
				skipNextCloseSearchLoadRef.current = queryRef.current.length > 0;
				return;
			}

			loadOptionsOnOpen();
		},
		[loadOptionsOnOpen]
	);

	const handleSearchChange = React.useCallback(
		(value: string) => {
			const shouldSkipLoad = skipNextCloseSearchLoadRef.current && value.length === 0;
			skipNextCloseSearchLoadRef.current = false;
			if (shouldSkipLoad) {
				return;
			}
			queryRef.current = value;
			hasNextPageRef.current = false;
			paginationRef.current = undefined;
			setInternalPagination({ hasNextPage: false });
			onSearchChange?.(value);
			void runLoadOptions(value, undefined, 'options', { hasNextPage: false });
		},
		[onSearchChange, runLoadOptions]
	);

	return {
		handleLastOptionRef,
		handleOpenChange,
		handleSearchChange,
		isLoadingNextPage: loadStatus === 'loading' && loadOperation === 'pagination',
		loadNextPage,
		loadStatus,
	};
};
